'use client';

import { ChevronsUpDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@components/ui/sidebar';
import { UserOrg } from '../utils/org-utils';

import { useTranslations } from 'next-intl';
import AppAvatar from './AppAvatar';
import { useOrganizationSwitch } from '@hooks/use-organization-switch';

type OrgSwitchProps = {
  orgs: UserOrg[];
  organization_id?: string;
  dropdownSide?: 'bottom' | 'right';
};

export function OrgSwitcher({
  orgs,
  organization_id,
  dropdownSide = 'right'
}: OrgSwitchProps) {
  const t = useTranslations();
  const { isMobile } = useSidebar();
  let active: UserOrg | undefined = orgs?.length ? orgs[0] : undefined;
  if (organization_id) {
    active = orgs.find(org => org?.organization?.id === organization_id);
  }
  const activeOrg = active;
  const { switching, switchOrganization } =
    useOrganizationSwitch(organization_id);

  function kbShortcut(index: number) {
    const shouldRender = false;
    if (shouldRender) {
      return <DropdownMenuShortcut>⌘{index}</DropdownMenuShortcut>;
    }
    return null;
  }
  const orgName = activeOrg?.organization?.name;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="h-auto min-h-12 px-2 py-1.5 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-md border border-sidebar-border bg-transparent text-sidebar-foreground">
                <AppAvatar name={orgName} />
              </div>
              <div className="grid flex-1 text-left">
                <span className="truncate text-label">{orgName}</span>
                <span className="truncate text-meta text-muted-foreground">
                  {activeOrg?.user_organization?.position ?? ''}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : dropdownSide}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-column tracking-wider text-muted-foreground uppercase">
              {t('common.organizations')}
            </DropdownMenuLabel>
            {orgs?.map((org, index) => (
              <DropdownMenuItem
                key={`${org.organization.name}-${index}`}
                onClick={() => void switchOrganization(org.organization.id)}
                disabled={switching}
                aria-label={
                  org.organization.id === activeOrg?.organization.id
                    ? `${org.organization.name}, ${t('common.current')}`
                    : org.organization.name
                }
                className="gap-2 p-2"
              >
                <AppAvatar name={org.organization.name} />
                {org.organization.name}
                {kbShortcut(index + 1)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
